import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';

import { ReportFilters, ReportFrequency, ReportType, Subscription } from '../modules/admin-reports/types/report-subscriptions';

enum QueryKey {
	Subscriptions = 'subscriptions',
}

type SubscriptionsAPIData = {
	records: Subscription[];
};

type CreateSubscriptionAPIData = {
	includeArchivedUsers: boolean;
	frequency: ReportFrequency;
	reportType: ReportType;
	customPropertyGroup?: string;
	filters?: ReportFilters;
};

type UpdateSubscriptionAPIData = {
	id: string;
	includeArchivedUsers?: boolean;
	customPropertyGroup?: string;
	filters?: ReportFilters;
};

type MutateSubscriptionOptions = {
	onSuccess?: () => void;
	onError?: (error: unknown) => void;
};

export const useCreateSubscription = (options: MutateSubscriptionOptions = {}) => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: (data: CreateSubscriptionAPIData) =>
			http.post('/report_email_subscriptions', data, {
				headers: { 'Key-Inflection': 'camel' },
			}),
		...options,
	});
};

export const useDeleteSubscription = (options: MutateSubscriptionOptions = {}) => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: (id: string) => http.delete(`/report_email_subscriptions/${id}`),
		...options,
	});
};

export const useDeleteAllSubscriptions = (options: MutateSubscriptionOptions = {}) => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: () => http.delete('/report_email_subscriptions/delete_all'),
		...options,
	});
};

export const useUpdateSubscription = (options: MutateSubscriptionOptions = {}) => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: (data: UpdateSubscriptionAPIData) =>
			http.put(`/report_email_subscriptions/${data.id}`, data, {
				headers: { 'Key-Inflection': 'camel' },
			}),
		...options,
	});
};

const createSubscriptionsFetcher = (http: HttpClient) => async () => {
	const res = await http.get<SubscriptionsAPIData>('/report_email_subscriptions', {
		headers: { 'Key-Inflection': 'camel' },
	});

	return res.data.records;
};

export const useSubscriptions = (options: UseQueryOptions<Subscription[], Error> = {}) => {
	return useQuery({
		queryKey: [QueryKey.Subscriptions],
		queryFn: createSubscriptionsFetcher(useHttpClient()),
		...options,
	});
};

export const loadSubscriptions = async () => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: [QueryKey.Subscriptions],
		queryFn: createSubscriptionsFetcher(http),
	});
};

// Cache management
export const useInvalidateSubscriptions = () => {
	const client = useQueryClient();

	return useCallback(() => {
		client.invalidateQueries([QueryKey.Subscriptions]);
	}, [client]);
};

// Optimistically mutate the cached subscriptions so the UI reflects an edit before the
// server round-trip/refetch lands. Prevents rapid successive edits from diffing against
// stale data (e.g. two quick filter-chip removals resurrecting a just-removed value).
export const useUpdateSubscriptionsCache = () => {
	const client = useQueryClient();

	return useCallback(
		(updater: (subscriptions: Subscription[]) => Subscription[]) => {
			client.setQueryData<Subscription[]>([QueryKey.Subscriptions], (current) => (current ? updater(current) : current));
		},
		[client]
	);
};
